import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft, X, ChevronLeft, ChevronRight, Mail, Ban, Gift, ImageIcon, Video, Pencil, Sparkles, Monitor, Headphones, TrendingDown, Target, Clapperboard, MessageCircle, Users, BarChart3, BrainCircuit, Send } from 'lucide-react';
import AppleHeader from '../components/AppleHeader';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';

// Define our subscription plan types
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  max_fotos_mes: number;
}

export const Paywall = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setMobileTitleOverride } = useSidebar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [activePlanIndex, setActivePlanIndex] = useState<number>(1); // Default to Negocio plan (middle)
    const [promoMs, setPromoMs] = useState<number>(0);
  const [shortName, setShortName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [showPromoCountdown, setShowPromoCountdown] = useState<boolean>(false);
  
  // New states for subscription flow
  const [subscriptionStep, setSubscriptionStep] = useState<'initial' | 'email' | 'payment'>('initial');
  const [cancellingSubscription, setCancellingSubscription] = useState<boolean>(false);
  const [cancelSuccess, setCancelSuccess] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showMpEmailTutorial, setShowMpEmailTutorial] = useState<boolean>(false);
  const [mpEmailTutorialStep, setMpEmailTutorialStep] = useState<1 | 2>(1);
  const [showContactQR, setShowContactQR] = useState<string>('');

  // Override sidebar mobile title so it doesn't show "Estudio virtual"
  useEffect(() => {
    setMobileTitleOverride(' ');
    return () => { setMobileTitleOverride(null); };
  }, [setMobileTitleOverride]);

  // Country/provider settings
  const [pais, setPais] = useState<string>('Argentina');
  const normalizedPais = (pais || '').toLowerCase();
  const isArgentina = normalizedPais === 'argentina';
  const isChile = normalizedPais === 'chile';
  const isColombia = normalizedPais === 'colombia';
  const isMexico = normalizedPais === 'méxico' || normalizedPais === 'mexico';
  const isPeru = normalizedPais === 'perú' || normalizedPais === 'peru';

  const currencyCode = isArgentina
    ? 'ARS'
    : isChile
    ? 'CLP'
    : isColombia
    ? 'COP'
    : isMexico
    ? 'MXN'
    : isPeru
    ? 'PEN'
    : 'USD';

  const providerName = isArgentina ? 'Mercado Pago' : 'Stripe';
  const providerLogoUrl = isArgentina
    ? 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//mercado-pago-logo.png'
    : 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/stripe-logotipo.png';

  const getPlanPrice = (planId: string, arsPrice: number) => {
    if (isArgentina) return arsPrice;

    if (isChile) {
      switch (planId) {
        case 'emprendimiento': return 14999;
        case 'negocio': return 49999;
        case 'enterprise': return 149999;
        default: return 0;
      }
    }

    if (isColombia) {
      switch (planId) {
        case 'emprendimiento': return 59999;   // $59.999 COP
        case 'negocio': return 199000;        // $199.000 COP
        case 'enterprise': return 550000;     // $550.000 COP
        default: return 0;
      }
    }

    if (isMexico) {
      switch (planId) {
        case 'emprendimiento': return 299;    // $299 MXN
        case 'negocio': return 999;          // $999 MXN
        case 'enterprise': return 2999;      // $2.999 MXN
        default: return 0;
      }
    }

    if (isPeru) {
      switch (planId) {
        case 'emprendimiento': return 50;     // 50 PEN
        case 'negocio': return 180;          // 180 PEN
        case 'enterprise': return 550;       // 550 PEN
        default: return 0;
      }
    }

    // Default: USD
    switch (planId) {
      case 'emprendimiento': return 25;
      case 'negocio': return 50;
      case 'enterprise': return 150;
      default: return 0;
    }
  };

  const formatPrice = (amount: number) => {
    const locale = isArgentina
      ? 'es-AR'
      : isChile
      ? 'es-CL'
      : isColombia
      ? 'es-CO'
      : isMexico
      ? 'es-MX'
      : isPeru
      ? 'es-PE'
      : 'en-US';

    const fmt = new Intl.NumberFormat(locale, { minimumFractionDigits: 0 });
    return `$${fmt.format(amount)} ${currencyCode}`;
  };

  // Plans configuration
  const plans: SubscriptionPlan[] = [
    {
      id: "emprendimiento",
      name: "Básico",
      price: 21999,
      description: "Pensado para pequeños emprendimientos que están buscando profesionalizar su negocio",
      features: [
        "Hasta 60 imágenes por mes",
        "Acceso desde computadora, tablet y celular",
        "~Generación de videos con IA",
        "~Función de Edición de imágenes con IA"
      ],
      max_fotos_mes: 60
    },
    {
      id: "negocio",
      name: "Pro",
      price: 69999,
      description: "Ideal para negocios en crecimiento que quieren imágenes de alta calidad para impulsar sus ventas",
      features: [
        "Hasta 250 imágenes por mes",
        "Generación de videos con IA",
        "Función de Edición de imágenes con IA",
        "Acceso desde computadora, tablet y celular",
        "Soporte prioritario"
      ],
      max_fotos_mes: 250
    },
    {
      id: "enterprise",
      name: "Ultra",
      price: 209999,
      description: "Creado para negocios con producción de imágenes y gestión catálogos grandes",
      features: [
        "Hasta 900 imágenes por mes",
        "Generación de videos con IA",
        "Videos pro (videos ultra realistas)",
        "Función de Edición de imágenes con IA",
        "Acceso desde computadora, tablet y celular",
        "Soporte dedicado 24/7",
        "Mejor relación precio / imagen",
        "Asesoría estratégica para campañas"
      ],
      max_fotos_mes: 900
    },
    {
      id: "experto",
      name: "Experto",
      price: 0,
      description: "Contratá un experto en marketing e IA de Atlas para que administre tus redes, liberando tu tiempo y preocupaciones",
      features: [
        "Manager de redes dedicado",
        "Estrategia de contenido personalizada",
        "Publicaciones y gestión diaria",
        "Reportes de rendimiento mensuales",
        "Imágenes y videos con IA incluidos",
        "Soporte dedicado 24/7"
      ],
      max_fotos_mes: 0
    }
  ];

  useEffect(() => {
    fetchUserProfile();
    
    // Ensure all desktop cards have the same height
    const timer = setTimeout(() => equalizeCardHeights(), 100);
    const timer2 = setTimeout(() => equalizeCardHeights(), 400);
    const timer3 = setTimeout(() => equalizeCardHeights(), 800);
    
    window.addEventListener('resize', equalizeCardHeights);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', equalizeCardHeights);
    };
  }, []);

  // Start a simple 57:00 countdown when page loads (non-persistent across navigations)
  useEffect(() => {
    setPromoMs(57 * 60 * 1000);
    const id = setInterval(() => {
      setPromoMs(prev => (prev <= 1000 ? 0 : prev - 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  
  
  // Function to ensure all cards have equal heights (desktop only)
  const equalizeCardHeights = () => {
    // Only equalize on desktop (md breakpoint = 768px)
    if (window.innerWidth < 768) return;
    
    const cards = document.querySelectorAll('.plan-card');
    if (cards.length === 0) return;
    
    // Reset heights first
    cards.forEach(card => {
      (card as HTMLElement).style.height = 'auto';
      (card as HTMLElement).style.minHeight = '0';
    });
    
    // Find the tallest card
    let maxHeight = 0;
    cards.forEach(card => {
      const height = card.getBoundingClientRect().height;
      maxHeight = Math.max(maxHeight, height);
    });
    
    const h = Math.ceil(maxHeight);
    // Apply the height to all cards
    cards.forEach(card => {
      (card as HTMLElement).style.height = `${h}px`;
      (card as HTMLElement).style.minHeight = `${h}px`;
    });
  };

  const featureIcon = (text: string, disabled: boolean) => {
    const cls = `w-4 h-4 ${disabled ? 'text-neutral-300' : 'text-neutral-600'} mr-1.5 md:mr-2 mt-0.5 flex-shrink-0`;
    if (disabled) return <X className={cls} />;
    if (text.startsWith('Hasta ') && text.includes('imágenes por mes')) return <ImageIcon className={cls} />;
    if (text.includes('Videos pro')) return <Clapperboard className={cls} />;
    if (text.includes('Generación de videos')) return <Video className={cls} />;
    if (text.includes('Edición de imágenes')) return <Pencil className={cls} />;
    if (text.includes('Modo Pro')) return <Sparkles className={cls} />;
    if (text.includes('computadora, tablet')) return <Monitor className={cls} />;
    if (text.includes('Soporte')) return <Headphones className={cls} />;
    if (text.includes('relación precio')) return <TrendingDown className={cls} />;
    if (text.includes('Asesoría')) return <Target className={cls} />;
    if (text.includes('Manager de redes')) return <Users className={cls} />;
    if (text.includes('Estrategia de contenido')) return <Target className={cls} />;
    if (text.includes('Publicaciones') && text.includes('gestión')) return <Send className={cls} />;
    if (text.includes('Reportes de rendimiento')) return <BarChart3 className={cls} />;
    if (text.includes('Imágenes y videos con IA')) return <BrainCircuit className={cls} />;
    return <CheckCircle2 className={cls} />;
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id_company, name_short')
        .eq('id', user.id)
        .single();
        
      if (profileError) throw profileError;
      
      if (!profileData.id_company) throw new Error('No hay una empresa asociada a este usuario');
      
      const { data: company, error: companyError } = await supabase
        .from('company_profiles')
        .select('plan, max_fotos_mes, pais, nombre, imagenes_totales_generadas')
        .eq('id_company', profileData.id_company)
        .single();
        
      if (companyError) throw companyError;
      
      setProfile({
        ...profileData,
        user_id: user.id
      });
      if ((profileData as any)?.name_short) setShortName((profileData as any).name_short as string);
      if ((company as any)?.nombre) setCompanyName((company as any).nombre as string);
      
      setCurrentPlan(company.plan);
      if ((company as any)?.pais) setPais((company as any).pais as string);

      const total = (company as any)?.imagenes_totales_generadas as number | undefined;
      setShowPromoCountdown(typeof total === 'number' ? total < 22 : false);
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
      setError(err.message || 'Error al cargar la información del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscription = async (planId: string) => {
    try {
      setProcessing(true);
      setError(null);
      
      const selectedPlan = plans.find(plan => plan.id === planId);
      if (!selectedPlan) throw new Error('Plan no encontrado');
      
      if (isArgentina) {
        // Argentina: navigate to dedicated checkout page
        navigate('/checkout', { state: { planId } });
        return;
      }
      
      // Non-Argentina: show payment modal
      setSubscriptionStep('initial');
      setSelectedPlanId(planId);
      setShowPaymentModal(true);
      
    } catch (err: any) {
      console.error('Error processing subscription:', err);
      setError(err.message || 'Error al procesar la suscripción');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleNextStep = () => {
    if (subscriptionStep === 'initial') {
      if (!isArgentina) {
        // For Stripe (non-Argentina), go straight to checkout
        handleContinueToCheckout();
      }
    }
  };

  const handleContinueToCheckout = async () => {
    try {
      setProcessing(true);
      setShowPaymentModal(false);
      
      const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
      if (!selectedPlan) throw new Error('Plan no encontrado');
      
      setSuccess(`Procesando redirección a ${providerName}...`);
      
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no encontrado');
      
      // Send request to the provided API endpoint
      const endpoint = isArgentina
        ? 'https://ai-agency-dt7d.onrender.com/webhook/08741f54-6bda-4751-8054-14a59f8338ca'
        : 'https://ai-agency-dt7d.onrender.com/webhook/stripe-start';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({
          user_id: user.id,
          plan: selectedPlanId
        }),
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener el enlace de checkout');
      }
      
      const data = await response.json();
      
      // Redirect to the checkout URL returned by provider
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No se recibió una URL válida para el checkout');
      }
    } catch (err: any) {
      console.error('Error redirecting to checkout:', err);
      setError(err.message || `Error al redirigir al checkout de ${providerName}`);
      setProcessing(false);
    }
  };
  
  // Opens the cancel confirmation modal
  const handleOpenCancelModal = () => {
    setShowCancelModal(true);
  };

  // Close the cancel confirmation modal
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
  };
  
  // Handler for cancel subscription button after confirmation
  const handleCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      setError(null);
      setShowCancelModal(false); // Close the modal
      
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no encontrado');
      
      // Send request to the cancel subscription endpoint
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/ba02b716-44a0-44e7-a3b7-72c3f5ccfbcd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id
        }),
      });
      
      if (!response.ok) {
        throw new Error('Error al cancelar la suscripción');
      }
      
      const data = await response.json();
      
      if (data.response === 'success') {
        // Update UI to show success
        setCancelSuccess(true);
        setSuccess('Tu suscripción ha sido cancelada exitosamente.');
        
        // Refresh user profile to get updated plan status
        setTimeout(() => {
          fetchUserProfile();
        }, 2000);
      } else {
        throw new Error('Error al procesar la cancelación');
      }
    } catch (err: any) {
      console.error('Error cancelling subscription:', err);
      setError(err.message || 'Error al cancelar la suscripción');
    } finally {
      setCancellingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="px-4 py-2 md:p-8 w-full max-w-full">
      {/* Mobile: title only, no back button (sidebar provides it) */}
      <div className="sm:hidden">
        <AppleHeader title={currentPlan !== 'gratis' ? 'Mejora tu plan' : 'Elegí tu plan'} />
      </div>
      {/* Desktop: title + back button */}
      <div className="hidden sm:block relative">
        <AppleHeader title={currentPlan !== 'gratis' ? 'Mejora tu plan' : 'Elegí tu plan'} onBack={() => navigate(-1)} backLabel="Volver" />
      </div>
      <p className="text-[13px] text-neutral-500 text-center -mt-2 mb-6 sm:mb-8">Aumenta tus ventas como ya lo hicieron cientos de marcas</p>
      
      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700 mx-1">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2.5 text-[15px] text-green-700 mx-1">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      
      {/* Removed navigation arrows as requested */}

      {/* Mobile: horizontal plan selector tabs */}
      {(() => {
        const filteredPlans = plans.filter((plan) => {
          if (plan.id === 'emprendimiento' && (currentPlan === 'negocio' || currentPlan === 'enterprise')) return false;
          return true;
        });
        return (
          <>
            <div className="md:hidden flex rounded-md bg-surface-elevated p-1 mb-4 mt-2">
              {filteredPlans.map((plan, idx) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setActivePlanIndex(idx)}
                  className={`flex-1 py-2 text-[13px] font-medium rounded-sm transition-colors ${
                    activePlanIndex === idx
                      ? 'bg-surface-card text-brand-black/90 shadow-sm'
                      : 'text-neutral-500'
                  }`}
                >
                  {plan.name}
                </button>
              ))}
            </div>
            {/* Mobile: sliding cards */}
            <div className="md:hidden overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${activePlanIndex * 100}%)` }}
              >
              {filteredPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="w-full flex-shrink-0 px-0.5"
                >
                <div
                  className={`plan-card border rounded-lg w-full bg-surface-card ${currentPlan === plan.id ? 'border-brand-primary' : 'border-neutral-200'} flex flex-col relative`}
                >
            <div className="px-5 py-4 md:p-5 flex flex-col h-full">
              <h3 className="text-[17px] font-bold text-brand-black/90">{plan.name}</h3>
              
              <p className="text-[13px] text-neutral-500 mb-4 mt-1.5">{plan.description}</p>
              
              {plan.id !== 'experto' && (
              <p className="text-[17px] font-bold text-brand-black/90 mb-5">
                {!isArgentina && (plan.id === 'negocio' || plan.id === 'enterprise') ? (
                  <>
                    <span className="flex items-center justify-start gap-2">
                      <span className="text-[13px] font-normal text-neutral-400 line-through">
                        {formatPrice(getPlanPrice(plan.id, plan.price))}
                      </span>
                      <span className="text-[11px] font-semibold text-white px-2 py-0.5 rounded-sm bg-brand-primary">50% OFF</span>
                    </span>
                    <span className="block">
                      {formatPrice(Math.floor(getPlanPrice(plan.id, plan.price) / 2))}
                      <span className="text-[13px] font-normal text-neutral-500"> el primer mes</span>
                    </span>
                  </>
                ) : (
                  <>
                    {formatPrice(getPlanPrice(plan.id, plan.price))}
                    <span className="text-[13px] font-normal text-neutral-500">/mes</span>
                  </>
                )}
              </p>
              )}
              
              {plan.id === 'experto' ? (
                <a
                  href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Quisiera saber sobre la contratación del plan Experto.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:hidden inline-flex items-center justify-center w-full h-10 px-4 rounded-md shadow-sm bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium transition-colors"
                >
                  Saber más
                </a>
              ) : (
                <button
                  onClick={() => handleSubscription(plan.id)}
                  disabled={processing || currentPlan === plan.id}
                  className={`inline-flex items-center justify-center w-full h-10 px-4 rounded-md shadow-sm text-[15px] font-medium transition-colors ${
                    currentPlan === plan.id 
                      ? 'bg-green-600 text-white cursor-default' 
                      : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                  }`}
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : currentPlan === plan.id ? (
                    'Plan Actual'
                  ) : (
                    'Subscribirse'
                  )}
                </button>
              )}
              {plan.id === 'experto' && (
                <button
                  type="button"
                  onClick={() => setShowContactQR(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Quisiera saber sobre la contratación del plan Experto.`)}
                  className="hidden md:inline-flex items-center justify-center w-full h-10 px-4 rounded-md shadow-sm bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium transition-colors"
                >
                  Saber más
                </button>
              )}
              {currentPlan === plan.id && currentPlan !== 'gratis' && plan.id !== 'experto' && (
                <button
                  onClick={handleOpenCancelModal}
                  disabled={cancellingSubscription || cancelSuccess}
                  className={`mt-2 flex items-center justify-center w-full h-8 px-4 rounded-md text-[13px] font-medium transition-colors ${
                    cancelSuccess 
                      ? 'bg-green-50 border border-green-200 text-green-700 cursor-default' 
                      : 'bg-surface-elevated border border-neutral-200 hover:bg-neutral-100 text-neutral-600'
                  }`}
                >
                  {cancellingSubscription ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : cancelSuccess ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Cancelada
                    </>
                  ) : (
                    'Cancelar suscripción'
                  )}
                </button>
              )}
              
              <hr className="border-neutral-200 mt-5 mb-5" />
              
              <ul className="flex-grow">
                {plan.features.map((feature, index) => {
                  const isDisabled = feature.startsWith('~');
                  const displayFeature = isDisabled ? feature.slice(1) : feature;
                  return (
                    <li key={index} className={`flex items-start mb-2 ${isDisabled ? 'text-neutral-300' : 'text-neutral-600'}`}>
                      {featureIcon(displayFeature, isDisabled)}
                      <span className="text-[13px]">{displayFeature}</span>
                    </li>
                  );
                })}
              </ul>

              {isArgentina && currentPlan !== plan.id && plan.id !== 'experto' && (
                <p className="text-[13px] text-brand-primary font-medium mt-4">
                  + {plan.id === 'emprendimiento' ? '30' : plan.id === 'negocio' ? '50' : '100'} imágenes extra de bienvenida
                </p>
              )}
              {!isArgentina && (plan.id === 'negocio' || plan.id === 'enterprise') && currentPlan !== plan.id && (
                <p className="text-[13px] font-bold text-brand-primary mt-4">
                  50% descuento primer mes
                </p>
              )}
              
            </div>
          </div>
              </div>
              ))}
              </div>
            </div>
            {/* Desktop: 4-column grid */}
            <div className="hidden md:grid md:grid-cols-4 md:gap-4 pt-4">
              {filteredPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`plan-card border rounded-lg w-full bg-surface-card ${currentPlan === plan.id ? 'border-brand-primary' : 'border-neutral-200'} flex flex-col relative`}
                >
                  {isArgentina && currentPlan !== plan.id && plan.id !== 'experto' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-[13px] font-semibold text-white shadow-sm whitespace-nowrap bg-brand-primary">
                        <Gift className="w-3.5 h-3.5 flex-shrink-0" />
                        {plan.id === 'emprendimiento' ? '30' : plan.id === 'negocio' ? '50' : '100'} fotos de bienvenida
                      </div>
                    </div>
                  )}
                  {!isArgentina && (plan.id === 'negocio' || plan.id === 'enterprise') && currentPlan !== plan.id && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-[13px] font-semibold text-white shadow-sm whitespace-nowrap bg-brand-primary">
                        <Gift className="w-3.5 h-3.5 flex-shrink-0" />
                        50% descuento primer mes
                      </div>
                    </div>
                  )}
                  <div className={`p-5 flex flex-col h-full ${plan.id !== 'experto' && ((isArgentina && currentPlan !== plan.id) || (!isArgentina && (plan.id === 'negocio' || plan.id === 'enterprise') && currentPlan !== plan.id)) ? 'pt-6' : ''}`}>
                    <h3 className="text-[17px] font-bold text-brand-black/90">{plan.name}</h3>
                    <p className="text-[13px] text-neutral-500 mb-4 mt-1.5">{plan.description}</p>
                    {plan.id !== 'experto' && (
                    <p className="text-[17px] font-bold text-brand-black/90 mb-5">
                      {!isArgentina && (plan.id === 'negocio' || plan.id === 'enterprise') ? (
                        <>
                          <span className="flex items-center justify-start gap-2">
                            <span className="text-[13px] font-normal text-neutral-400 line-through">
                              {formatPrice(getPlanPrice(plan.id, plan.price))}
                            </span>
                            <span className="text-[11px] font-semibold text-white px-2 py-0.5 rounded-sm bg-brand-primary">50% OFF</span>
                          </span>
                          <span className="block">
                            {formatPrice(Math.floor(getPlanPrice(plan.id, plan.price) / 2))}
                            <span className="text-[13px] font-normal text-neutral-500"> el primer mes</span>
                          </span>
                        </>
                      ) : (
                        <>
                          {formatPrice(getPlanPrice(plan.id, plan.price))}
                          <span className="text-[13px] font-normal text-neutral-500">/mes</span>
                        </>
                      )}
                    </p>
                    )}
                    {plan.id === 'experto' && <div className="mb-12" />}
                    {plan.id === 'experto' ? (
                      <button
                        type="button"
                        onClick={() => setShowContactQR(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Quisiera saber sobre la contratación del plan Experto.`)}
                        className="inline-flex items-center justify-center w-full h-10 px-4 rounded-md shadow-sm bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium transition-colors"
                      >
                        Saber más
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSubscription(plan.id)}
                        disabled={processing || currentPlan === plan.id}
                        className={`inline-flex items-center justify-center w-full h-10 px-4 rounded-md shadow-sm text-[15px] font-medium transition-colors ${
                          currentPlan === plan.id
                            ? 'bg-green-600 text-white cursor-default'
                            : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                        }`}
                      >
                        {processing ? (
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        ) : currentPlan === plan.id ? (
                          'Plan Actual'
                        ) : (
                          'Subscribirse'
                        )}
                      </button>
                    )}
                    {currentPlan === plan.id && currentPlan !== 'gratis' && plan.id !== 'experto' && (
                      <button
                        onClick={handleOpenCancelModal}
                        disabled={cancellingSubscription || cancelSuccess}
                        className={`mt-2 flex items-center justify-center w-full h-8 px-4 rounded-md text-[13px] font-medium transition-colors ${
                          cancelSuccess
                            ? 'bg-green-50 border border-green-200 text-green-700 cursor-default'
                            : 'bg-surface-elevated border border-neutral-200 hover:bg-neutral-100 text-neutral-600'
                        }`}
                      >
                        {cancellingSubscription ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : cancelSuccess ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Cancelada
                          </>
                        ) : (
                          'Cancelar suscripción'
                        )}
                      </button>
                    )}
                    
                    <hr className="border-neutral-200 mt-5 mb-5" />
                    
                    <ul className="flex-grow">
                      {plan.features.map((feature, index) => {
                        const isDisabled = feature.startsWith('~');
                        const displayFeature = isDisabled ? feature.slice(1) : feature;
                        return (
                          <li key={index} className={`flex items-start mb-2 ${isDisabled ? 'text-neutral-300' : 'text-neutral-600'}`}>
                            {featureIcon(displayFeature, isDisabled)}
                            <span className="text-[13px]">{displayFeature}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      })()}
      
      
      {/* Mobile: WhatsApp help button centered — before info section */}
      <div className="mt-8 flex justify-center md:hidden">
        <a
          href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Me contactaba porque tenia una duda.`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-surface-card border border-neutral-200 text-[15px] font-medium text-neutral-600 hover:bg-neutral-100 shadow-sm transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          Tenés alguna duda?
        </a>
      </div>

      <div className="mt-6 md:mt-12 flex flex-col md:flex-row md:items-start md:justify-between gap-6 md:pr-32">
        <div className="max-w-2xl mx-auto md:mx-0">
          <p className="text-[11px] text-neutral-400 text-center md:text-left mb-3 uppercase tracking-wider font-medium">Información importante</p>
          <div className="grid grid-cols-1 gap-2.5">
            <div className="flex items-start gap-2.5 text-[13px] text-neutral-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-300 mt-0.5 flex-shrink-0" />
              <span>Las suscripciones se renuevan automáticamente cada mes.</span>
            </div>
            <div className="flex items-start gap-2.5 text-[13px] text-neutral-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-300 mt-0.5 flex-shrink-0" />
              <span>Puedes cancelar tu suscripción en cualquier momento.</span>
            </div>
            <div className="flex items-start gap-2.5 text-[13px] text-neutral-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-300 mt-0.5 flex-shrink-0" />
              <span>Al cambiar de plan, se aplicará el nuevo límite de fotos inmediatamente.</span>
            </div>
            <div className="flex items-start gap-2.5 text-[13px] text-neutral-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-300 mt-0.5 flex-shrink-0" />
              <span>Los pagos son procesados de forma segura por {providerName}.</span>
            </div>
          </div>
        </div>

        {/* Desktop: help button to the right of info section */}
        <button
          type="button"
          onClick={() => setShowContactQR(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Me contactaba porque tenia una duda.`)}
          className="hidden md:inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-surface-card border border-neutral-200 text-[15px] font-medium text-neutral-600 hover:bg-neutral-100 shadow-sm transition-colors flex-shrink-0 self-center"
        >
          <MessageCircle className="w-4 h-4" />
          Tenés alguna duda?
        </button>
      </div>
      
      {/* Cancel Subscription Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm flex items-start md:items-center justify-center z-50 p-4 overflow-y-auto pt-10">
          <div className="bg-surface-card rounded-lg shadow-md max-w-md w-full p-5 relative my-4 md:my-0">
            <button 
              onClick={handleCloseCancelModal}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="mb-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-md bg-red-50 border border-red-200 flex items-center justify-center mb-4">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">Confirmar cancelación</h3>
              <p className="text-[15px] text-neutral-500">
                ¿Estás seguro de que deseas cancelar tu suscripción? Al cancelar, perderás acceso a las funciones premium.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end gap-2.5">
              <button
                onClick={handleCloseCancelModal}
                className="h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-100 transition-colors"
              >
                Volver
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors flex items-center justify-center"
              >
                {cancellingSubscription ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : (
                  'Confirmar cancelación'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Subscription Modal (Stripe / non-Argentina only) */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-surface-card rounded-lg shadow-md max-w-md w-full p-5 relative">
            <button 
              onClick={() => {
                setShowPaymentModal(false);
                setSubscriptionStep('initial');
              }}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-4 text-center">
              Finaliza tu suscripción
            </h3>
            
            {selectedPlanId && (
              <div className="mb-5 border-b border-neutral-200 pb-4">
                <p className="text-[13px] text-neutral-500 mb-1">Plan seleccionado:</p>
                <p className="text-[15px] font-semibold text-brand-black/90">
                  {plans.find(p => p.id === selectedPlanId)?.name}
                </p>
                {(() => {
                  const selected = plans.find(p => p.id === selectedPlanId);
                  const regular = getPlanPrice(selectedPlanId, selected?.price ?? 0);
                  const hasFirstMonthDiscount = selectedPlanId === 'negocio' || selectedPlanId === 'enterprise';
                  if (hasFirstMonthDiscount) {
                    const discounted = Math.floor(regular / 2);
                    return (
                      <>
                        <p className="text-[17px] font-bold text-brand-black/90 mt-2">
                          {formatPrice(discounted)}
                          <span className="text-[13px] font-normal text-neutral-500"> el primer mes</span>
                        </p>
                        <p className="text-[13px] text-neutral-500 mt-1">
                          Después {formatPrice(regular)} <span className="font-normal text-neutral-500">/mes</span>
                        </p>
                      </>
                    );
                  }
                  return (
                    <p className="text-[17px] font-bold text-brand-black/90 mt-2">
                      {formatPrice(regular)}<span className="text-[13px] font-normal text-neutral-500">/mes</span>
                    </p>
                  );
                })()}
              </div>
            )}
            
            <div className="mb-4">
              <div className="flex items-center justify-between p-3 rounded-md bg-brand-primary/5 border border-brand-primary/20">
                <p className="text-[13px] text-brand-primary font-medium">Pago seguro por {providerName}</p>
                <img src={providerLogoUrl} alt={providerName} className="h-5 w-auto ml-3" />
              </div>
            </div>
            
            <div className="mb-5">
              <p className="text-[13px] text-neutral-500 mb-4">
                Al hacer clic en "Continuar", serás redirigido a {providerName} para completar tu suscripción de manera segura.
              </p>
              
              <button
                onClick={handleNextStep}
                className="w-full h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
              >
                Continuar a pago seguro
              </button>
            </div>
            
            <div className="mt-4">
              <p className="text-[11px] text-neutral-400 text-center">
                Al continuar aceptas los <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline">términos y condiciones</a> de {providerName}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mercado Pago Email Tutorial Modal */}
      {showMpEmailTutorial && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm flex items-center justify-center z-[60] px-6 py-4">
          <div className="bg-surface-card rounded-lg shadow-md max-w-sm w-full p-5 relative">
            <button
              onClick={() => {
                setShowMpEmailTutorial(false);
                setShowPaymentModal(true);
              }}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            
            {mpEmailTutorialStep === 1 ? (
              <>
                <div className="mb-4">
                  <img
                    src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/mp_como_ver_mi_mail.png"
                    alt="Paso 1: Pantalla de inicio de Mercado Pago"
                    className="w-full max-h-96 object-contain rounded-md"
                  />
                </div>
                <p className="text-[13px] text-neutral-500 mb-4">
                  Para conocer qué correo tienes asociado a tu cuenta de Mercado Pago, entra a la aplicación y en la pantalla de inicio haz click en la esquina superior izquierda.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setMpEmailTutorialStep(2)}
                    className="w-full h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                  >
                    Siguiente
                  </button>
                  <button
                    onClick={() => {
                      setShowMpEmailTutorial(false);
                      setShowPaymentModal(true);
                    }}
                    className="w-full h-10 px-4 border border-neutral-200 bg-surface-card text-brand-black/90 rounded-md text-[15px] font-medium hover:bg-neutral-100 transition-colors"
                  >
                    Atrás
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <img
                    src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/mp_como_ver_mi_mail2.png"
                    alt="Paso 2: Ver correo en Mercado Pago"
                    className="w-full max-h-96 object-contain rounded-md"
                  />
                </div>
                <p className="text-[13px] text-neutral-500 mb-4">
                  En la sección de abajo, debajo de tu nombre podrás ver el correo asociado a tu cuenta.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      setShowMpEmailTutorial(false);
                      setShowPaymentModal(true);
                    }}
                    className="w-full h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                  >
                    Entendido
                  </button>
                  <button
                    onClick={() => setMpEmailTutorialStep(1)}
                    className="w-full h-10 px-4 border border-neutral-200 bg-surface-card text-brand-black/90 rounded-md text-[15px] font-medium hover:bg-neutral-100 transition-colors"
                  >
                    Atrás
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Contact QR Modal - Desktop only */}
      {showContactQR && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card rounded-lg shadow-md max-w-sm w-full p-5 relative">
            <button
              onClick={() => setShowContactQR('')}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1.5 text-center">Contactanos por WhatsApp</h3>
            <p className="text-[13px] text-neutral-500 mb-4 text-center">Escaneá el código QR con tu celular para abrir WhatsApp</p>
            <div className="flex justify-center mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://wa.me/5491122635392?text=${encodeURIComponent(showContactQR)}`)}`}
                alt="QR Code WhatsApp"
                className="w-48 h-48 rounded-md"
              />
            </div>
            <button
              onClick={() => setShowContactQR('')}
              className="w-full h-10 px-4 bg-surface-elevated border border-neutral-200 text-brand-black/90 rounded-md text-[15px] font-medium hover:bg-neutral-100 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Paywall;
